/**
 * Dynamic Type and font resolution.
 *
 * Point sizes come from Apple's Human Interface Guidelines, "Typography" >
 * "Specifications" > "iOS, iPadOS Dynamic Type sizes" and "Larger
 * accessibility type sizes" (https://developer.apple.com/design/human-interface-guidelines/typography).
 * A line is as tall as SwiftUI makes a `Text`: SF Pro's ascender plus
 * descender (`UIFont.lineHeight`, 1.1934 × the point size), rounded up to a
 * device pixel on a 3x iPhone (body 17 → 20.33, large title 34 → 40.67;
 * measured in the iOS 26.2 simulator). The HIG's tables list a taller
 * leading (body 22): the distance between a paragraph's lines (`LEADINGS`).
 */

import type { Platform } from '../protocol';
import type { DynamicTypeSize, FontFamily, LayoutEnvironment, ResolvedFont } from './types';

export type TextStyle =
  | 'largeTitle'
  | 'title'
  | 'title2'
  | 'title3'
  | 'headline'
  | 'body'
  | 'callout'
  | 'subheadline'
  | 'footnote'
  | 'caption'
  | 'caption2';

/** Size categories in the column order of `TEXT_STYLE_SIZES`. */
export const DYNAMIC_TYPE_SIZES: readonly DynamicTypeSize[] = [
  'xSmall',
  'small',
  'medium',
  'large',
  'xLarge',
  'xxLarge',
  'xxxLarge',
  'accessibility1',
  'accessibility2',
  'accessibility3',
  'accessibility4',
  'accessibility5',
];

const LARGE_INDEX = 3;

/**
 * Point size per text style and size category. Columns: xSmall, small,
 * medium, large, xLarge, xxLarge, xxxLarge, AX1, AX2, AX3, AX4, AX5.
 * (HIG "iOS, iPadOS Dynamic Type sizes" + "Larger accessibility type sizes".)
 */
export const TEXT_STYLE_SIZES: Record<TextStyle, readonly number[]> = {
  largeTitle: [31, 32, 33, 34, 36, 38, 40, 44, 48, 52, 56, 60],
  title: [25, 26, 27, 28, 30, 32, 34, 38, 43, 48, 53, 58],
  title2: [19, 20, 21, 22, 24, 26, 28, 34, 39, 44, 50, 56],
  title3: [17, 18, 19, 20, 22, 24, 26, 31, 37, 43, 49, 55],
  headline: [14, 15, 16, 17, 19, 21, 23, 28, 33, 40, 47, 53],
  body: [14, 15, 16, 17, 19, 21, 23, 28, 33, 40, 47, 53],
  callout: [13, 14, 15, 16, 18, 20, 22, 26, 32, 38, 44, 51],
  subheadline: [12, 13, 14, 15, 17, 19, 21, 25, 30, 36, 42, 49],
  footnote: [12, 12, 12, 13, 15, 17, 19, 23, 27, 33, 38, 44],
  caption: [11, 11, 11, 12, 14, 16, 18, 22, 26, 32, 37, 43],
  caption2: [11, 11, 11, 11, 13, 15, 17, 20, 24, 29, 34, 40],
};

/**
 * The macOS text styles (HIG "Typography" > "macOS built-in text styles"),
 * one size each: the Mac has no Dynamic Type. Body is 13.
 */
export const MAC_TEXT_STYLE_SIZES: Readonly<Record<TextStyle, number>> = {
  largeTitle: 26,
  title: 22,
  title2: 17,
  title3: 15,
  headline: 13,
  body: 13,
  callout: 12,
  subheadline: 11,
  footnote: 10,
  caption: 10,
  caption2: 10,
};

/**
 * The HIG's leading for the standard (Large) point sizes: the distance
 * between the baselines of a paragraph's lines, which SwiftUI keeps (a
 * multi-line body `Text` is 20.33 + 22 per further line); other sizes use
 * round(size * 1.2). An SF Symbol image takes a square of it.
 */
export const LEADINGS: Readonly<Record<number, number>> = {
  34: 41,
  28: 34,
  22: 28,
  20: 25,
  17: 22,
  16: 21,
  15: 20,
  13: 18,
  12: 16,
  11: 13,
};

export const FONT_WEIGHTS: Readonly<Record<string, number>> = {
  ultraLight: 100,
  thin: 200,
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  heavy: 800,
  black: 900,
};

/** CSS family stacks; keep in sync with the renderer's stylesheet. */
export const FONT_FAMILIES: Readonly<Record<FontFamily, string>> = {
  system:
    '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Inter, system-ui, sans-serif',
  rounded:
    'ui-rounded, "SF Pro Rounded", -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Inter, system-ui, sans-serif',
  monospaced:
    'ui-monospace, "SF Mono", Menlo, Monaco, "Cascadia Mono", "Roboto Mono", Consolas, monospace',
  serif: 'ui-serif, "New York", Georgia, "Times New Roman", serif',
};

const TEXT_STYLES = new Set<string>(Object.keys(TEXT_STYLE_SIZES));
const DESIGN_TO_FAMILY: Readonly<Record<string, FontFamily>> = {
  default: 'system',
  rounded: 'rounded',
  monospaced: 'monospaced',
  serif: 'serif',
};

export function isTextStyle(value: unknown): value is TextStyle {
  return typeof value === 'string' && TEXT_STYLES.has(value);
}

export function sizeCategoryIndex(size: DynamicTypeSize): number {
  const index = DYNAMIC_TYPE_SIZES.indexOf(size);
  return index < 0 ? LARGE_INDEX : index;
}

/** Point size of a text style at a Dynamic Type size category (on the Mac, its one size). */
export function textStyleSize(style: TextStyle, size: DynamicTypeSize, platform: Platform = 'ios'): number {
  if (platform === 'macos') return MAC_TEXT_STYLE_SIZES[style];
  const row = TEXT_STYLE_SIZES[style];
  return row[sizeCategoryIndex(size)] ?? row[LARGE_INDEX] ?? 17;
}

/** SF Pro's ascender plus descender per point (1950 + 494 of 2048 units per em). */
export const LINE_HEIGHT_RATIO = 2444 / 2048;

/** A line's height at a point size: `UIFont.lineHeight`, rounded up to a 3x pixel. */
export function lineHeightFor(size: number): number {
  return Math.ceil(size * LINE_HEIGHT_RATIO * 3 - 1e-6) / 3;
}

/** The distance between a paragraph's baselines at a point size. */
export function leadingFor(size: number): number {
  return LEADINGS[size] ?? Math.round(size * 1.2);
}

/** The height of `lines` lines of text in `font`: the first line, then its leading per further line. */
export function textHeight(lines: number, font: ResolvedFont): number {
  return lines > 0 ? font.lineHeight + (lines - 1) * font.leading : 0;
}

function formatPx(value: number): string {
  // Avoid long float tails from Dynamic Type scaling in the CSS string.
  return `${Math.round(value * 100) / 100}px`;
}

export function makeFont(
  family: FontFamily,
  size: number,
  weight: number,
  modifiers: { weightModifier?: number | undefined; familyModifier?: FontFamily | undefined; monospacedDigit?: boolean | undefined } = {},
): ResolvedFont {
  const lineHeight = lineHeightFor(size);
  const css = `${weight} ${formatPx(size)}/${formatPx(lineHeight)} ${FONT_FAMILIES[family]}`;
  const font: ResolvedFont = { family, size, weight, lineHeight, leading: leadingFor(size), css };
  if (modifiers.weightModifier !== undefined) font.weightModifier = modifiers.weightModifier;
  if (modifiers.familyModifier !== undefined) font.familyModifier = modifiers.familyModifier;
  if (modifiers.monospacedDigit) font.monospacedDigit = true;
  return font;
}

/** Body text for the current `dynamicTypeSize` (17/20.33 at Large; 13 on the Mac). */
export function defaultFont(env: LayoutEnvironment): ResolvedFont {
  return makeFont('system', textStyleSize('body', env.dynamicTypeSize, env.platform), 400);
}

/**
 * Apply a Font JSON object (`textStyle` | `size`, `weight`, `design`,
 * `relativeTo`) to an inherited font. A `textStyle` or `size` defines a new
 * font (weight defaults to regular, or semibold for `headline`; design to the
 * system family); without either, only the given `weight`/`design` change,
 * as a view modifier (`.fontWeight(.semibold)`, `.bold()`, `.fontDesign`)
 * that also applies to a new font set further in which names no weight or
 * design of its own: `.font(.system(size: 15)).fontWeight(.semibold)` is a
 * semibold 15.
 */
export function resolveFont(
  fontProps: unknown,
  inherited: ResolvedFont,
  env: LayoutEnvironment,
): ResolvedFont {
  if (typeof fontProps !== 'object' || fontProps === null) return inherited;
  const font = fontProps as Record<string, unknown>;
  const weightName = typeof font['weight'] === 'string' ? font['weight'] : undefined;
  const explicitWeight = weightName !== undefined ? FONT_WEIGHTS[weightName] : undefined;
  const design = typeof font['design'] === 'string' ? font['design'] : undefined;
  const explicitFamily = design !== undefined ? DESIGN_TO_FAMILY[design] : undefined;

  const textStyle = font['textStyle'];
  const fixedSize = font['size'];
  let size: number;
  let defaultWeight = 400;
  let isNewFont = false;
  if (isTextStyle(textStyle)) {
    size = textStyleSize(textStyle, env.dynamicTypeSize, env.platform);
    if (textStyle === 'headline') defaultWeight = 600;
    isNewFont = true;
  } else if (typeof fixedSize === 'number' && Number.isFinite(fixedSize) && fixedSize > 0) {
    size = fixedSize;
    const relativeTo = font['relativeTo'];
    // The Mac has no Dynamic Type: a fixed size stays as given.
    if (isTextStyle(relativeTo) && env.platform !== 'macos') {
      const base = TEXT_STYLE_SIZES[relativeTo][LARGE_INDEX] ?? 17;
      size = fixedSize * (textStyleSize(relativeTo, env.dynamicTypeSize) / base);
    }
    isNewFont = true;
  } else {
    size = inherited.size;
  }

  const weight = explicitWeight ?? (isNewFont ? (inherited.weightModifier ?? defaultWeight) : inherited.weight);
  const family = explicitFamily ?? (isNewFont ? (inherited.familyModifier ?? 'system') : inherited.family);
  // A weight or design without a new font is a modifier the text further in keeps.
  const weightModifier = !isNewFont && explicitWeight !== undefined ? explicitWeight : inherited.weightModifier;
  const familyModifier = !isNewFont && explicitFamily !== undefined ? explicitFamily : inherited.familyModifier;
  // Tabular digits stay on until a font says otherwise, like a weight modifier.
  const monospacedDigit = typeof font['monospacedDigit'] === 'boolean' ? font['monospacedDigit'] : inherited.monospacedDigit === true;
  if (
    !isNewFont &&
    weight === inherited.weight &&
    family === inherited.family &&
    weightModifier === inherited.weightModifier &&
    familyModifier === inherited.familyModifier &&
    monospacedDigit === (inherited.monospacedDigit === true)
  ) {
    return inherited;
  }
  return makeFont(family, size, weight, { weightModifier, familyModifier, monospacedDigit });
}
